import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DiscardDialog } from "@/components/DiscardDialog";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { adminUpdateStore } from "@/lib/api/admin";
import type { Store } from "@/lib/api/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { ScheduleInput } from "@/components/schedule/ScheduleInput";
import {
  scheduleToForm,
  formToSchedule,
  validateRawSchedule,
  type ScheduleFormState,
} from "@/lib/utils/schedule";

type StoreHoursDialogProps = {
  store: Store;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function StoreHoursDialog({ store, open, onOpenChange }: StoreHoursDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [schedule, setSchedule] = useState<ScheduleFormState>(() => scheduleToForm(store.schedule));
  const [discardOpen, setDiscardOpen] = useState(false);
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const initialScheduleRef = useRef(scheduleToForm(store.schedule));

  useEffect(() => {
    if (open) {
      const initial = scheduleToForm(store.schedule);
      initialScheduleRef.current = initial;
      setSchedule(initial);
      setScheduleErrors({});
      setApiError(null);
    }
  }, [open, store]);

  const mutation = useMutation({
    mutationFn: async () =>
      adminUpdateStore(user!, store.id, { schedule: formToSchedule(schedule) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store", store.id] });
      void qc.invalidateQueries({ queryKey: ["stores"] });
      toast.success("Business hours updated.");
      onOpenChange(false);
    },
    onError: (err) => setApiError((err as Error).message),
  });

  function tryClose() {
    if (JSON.stringify(schedule) !== JSON.stringify(initialScheduleRef.current)) {
      setDiscardOpen(true);
    } else {
      onOpenChange(false);
    }
  }

  function handleScheduleChange(v: ScheduleFormState) {
    setSchedule(v);
    setScheduleErrors({});
  }

  function handleSave() {
    const errs = validateRawSchedule(formToSchedule(schedule));
    if (errs) {
      setScheduleErrors(errs);
      return;
    }
    setScheduleErrors({});
    setApiError(null);
    mutation.mutate();
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Edit Business Hours</DialogTitle>
          </DialogHeader>

          <ScheduleInput value={schedule} onChange={handleScheduleChange} errors={scheduleErrors} />

          {apiError && <p className="text-sm text-destructive">{apiError}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={tryClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Spinner className="size-4 border-primary-foreground" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
